/**
 * Phase 2 — Beta scoping primitives.
 *
 * Single source of truth for "what counts toward the 13-step beta path"
 * across every Parent Hub surface (X/13 tiles, evidence strings, pipeline,
 * scorecard, learning mix).
 *
 * The 13 steps = 10 curated lessons + 3 hands-on modules.
 * - Lessons are matched by stable lesson_id from `launchLessonIds`.
 * - Modules are matched by `module_completions.module_key` against the
 *   three approved beta module keys.
 *
 * Do NOT keyword-match course names anywhere in the parent hub. If a new
 * lesson is added to the beta path, update `betaExperienceRoster.ts` and
 * the maps below — never widen by string match.
 */

import { launchLessonIds } from "@/lib/launch/launchExperienceRoster";

export type LaunchModuleId = "budget" | "credit" | "scam";

export const LAUNCH_PATH_LESSON_IDS: ReadonlySet<string> = new Set(launchLessonIds);

/** Total path length surfaced as "X / 13" everywhere. */
export const TOTAL_LAUNCH_PATH_STEPS = 13;

/** Lesson_id -> beta module assignment for the 10 curated lessons. */
export const MODULE_OF_LAUNCH_LESSON: Readonly<Record<string, LaunchModuleId>> = {
  "709b0a91-2103-4ddf-9a70-e4ee5dae0b41": "budget", // Needs vs Wants
  "f1ec70b9-2619-41b4-b2e8-f89425bf4e1b": "budget", // Opportunity Cost
  "9979b6d9-b308-49f4-b9a5-29e5a6a7ff3f": "budget", // Financial Tradeoffs
  "e29e634d-e035-42fb-8568-a5241a02d595": "budget", // Delayed Gratification
  "d2b87cb1-7774-4029-87e0-be51ab14840e": "budget", // Cash Flow
  "c6d62ce8-0b9e-40ff-b09d-64bcda1f6210": "credit", // Credit Score Basics
  "db6fdc3b-f16d-4fe5-a750-c65c2c8dc5a1": "credit", // Borrowing Money
  "fcbdef95-7194-4921-8cb7-dfdfebda0ee0": "credit", // Minimum Payments Trap
  "e1e29e49-0074-48f5-ba15-70374284867e": "scam",   // Urgency as a Red Flag
  "846aa833-7dd2-4059-892a-6fbebf6a2c2e": "scam",   // Too-Good-To-Be-True Returns
};

/** Reverse index: module -> lesson_id list. */
export const LAUNCH_LESSON_IDS_BY_MODULE: Readonly<Record<LaunchModuleId, readonly string[]>> = {
  budget: Object.entries(MODULE_OF_LAUNCH_LESSON).filter(([, m]) => m === "budget").map(([id]) => id),
  credit: Object.entries(MODULE_OF_LAUNCH_LESSON).filter(([, m]) => m === "credit").map(([id]) => id),
  scam: Object.entries(MODULE_OF_LAUNCH_LESSON).filter(([, m]) => m === "scam").map(([id]) => id),
};

/** module_completions.module_key values for the 3 beta modules. */
export const LAUNCH_MODULE_KEYS: Readonly<Record<LaunchModuleId, string>> = {
  budget: "budget-builder",
  credit: "credit-management",
  scam: "scam-defense",
};

/** Lookup helpers. */
export function moduleOfLaunchLesson(lessonId: string | null | undefined): LaunchModuleId | null {
  if (!lessonId) return null;
  return MODULE_OF_LAUNCH_LESSON[lessonId] ?? null;
}

export function isLaunchPathLessonId(lessonId: string | null | undefined): boolean {
  if (!lessonId) return false;
  return LAUNCH_PATH_LESSON_IDS.has(lessonId);
}

/** Module label used in tiles and evidence strings. */
export const LAUNCH_MODULE_LABEL: Readonly<Record<LaunchModuleId, string>> = {
  budget: "Money Foundations",
  credit: "Credit & Debt",
  scam: "Scam Defense",
};

/** Module display name (matches in-product module title). */
export const LAUNCH_MODULE_NAME: Readonly<Record<LaunchModuleId, string>> = {
  budget: "Budget Builder",
  credit: "Credit Management",
  scam: "Scam Defense",
};